/**
 * A Research Analyst's round avatar (V066): the admin-approved photo when there
 * is one, else the initials circle the app has always shown. The API hands out
 * a photo *key* ("<user id>/<16 hex>", null without an approved photo); the
 * image is public and cacheable at /public/ra-photo/<key>-{128,512}.webp.
 */
import { useState } from "react";

import { API_BASE } from "@/api/config";
import { cn } from "@/lib/cn";

import { initials } from "./people";

export type RaAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type RaAvatarTone = "violet" | "brand" | "muted";

const SIZE: Record<RaAvatarSize, { box: string; text: string; px: number }> = {
  xs: { box: "size-6", text: "text-[10px]", px: 24 },
  sm: { box: "size-8", text: "text-xs", px: 32 },
  md: { box: "size-9", text: "text-xs", px: 36 },
  lg: { box: "size-11", text: "text-sm", px: 44 },
  xl: { box: "size-24 sm:size-28", text: "text-3xl", px: 112 },
};

const TONE: Record<RaAvatarTone, string> = {
  violet: "bg-tint-violet text-[#6d4fd8] dark:text-[#b39dff]",
  brand: "bg-brand-soft text-brand",
  muted: "bg-surface-2 text-muted",
};

/** The approved photo's URL: 128 px for cards, 512 px for the profile. */
export function raPhotoUrl(key: string, size: 128 | 512 = 128): string {
  return `${API_BASE}/public/ra-photo/${key}-${size}.webp`;
}

export function RaAvatar({
  name,
  photo,
  size = "md",
  tone = "violet",
  className,
}: {
  name: string | null | undefined;
  /** The approved photo's key; null or missing: the initials. */
  photo?: string | null;
  size?: RaAvatarSize;
  tone?: RaAvatarTone;
  className?: string;
}) {
  // a photo that fails to load (removed since) falls back to the initials
  const [failed, setFailed] = useState<string | null>(null);
  const s = SIZE[size];
  if (photo && failed !== photo) {
    return (
      <img
        src={raPhotoUrl(photo, s.px > 64 ? 512 : 128)}
        alt={`${name ?? "Research Analyst"}, SEBI-registered Research Analyst`}
        width={s.px}
        height={s.px}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(photo)}
        className={cn(
          "shrink-0 rounded-full bg-surface-2 object-cover ring-1 ring-border",
          s.box,
          className,
        )}
      />
    );
  }
  return (
    <span
      aria-hidden
      data-testid="ra-initials"
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-bold",
        s.box,
        s.text,
        TONE[tone],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
